import React, { useState, useEffect } from "react";
import { X, DollarSign, FileText } from "lucide-react";

export default function PartialPaymentModal({
  isOpen,
  onClose,
  po,
  totalOwed = 0,
  paidAmount = 0,
  onSavePayment
}) {
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentNote, setPaymentNote] = useState("");
  const [error, setError] = useState("");

  const remainingBalance = Math.max(0, totalOwed - paidAmount);

  // Reset states when modal opens
  useEffect(() => {
    if (isOpen) {
      setPaymentAmount("");
      setPaymentNote("");
      setError("");
    }
  }, [isOpen]);

  if (!isOpen || !po) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");

    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      setError("Please enter a valid payment amount greater than $0.");
      return;
    }

    if (amount > remainingBalance + 0.01) {
      setError(`Payment cannot exceed the remaining balance of $${remainingBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.`);
      return;
    }

    if (!paymentNote.trim()) {
      setError("Please enter a payment memo or note (e.g. Check number, Bank reference).");
      return;
    }

    onSavePayment(amount, paymentNote.trim());
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: "500px" }}>
        <div className="modal-header">
          <h3 style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <DollarSign size={20} className="text-secondary" /> Record Payment
          </h3>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "1.5rem" }}>
          {error && (
            <div className="alert alert-danger" style={{ marginBottom: "1rem" }}>
              {error}
            </div>
          )}

          <div style={{ padding: "1rem", borderRadius: "6px", backgroundColor: "rgba(255, 255, 255, 0.02)", border: "1px solid var(--border-color)", marginBottom: "1.25rem" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1.5fr", gap: "0.5rem", fontSize: "0.9rem" }}>
              <span style={{ color: "var(--text-secondary)" }}>PO Number:</span>
              <strong style={{ color: "var(--text-primary)" }}>{po.poNumber}</strong>

              <span style={{ color: "var(--text-secondary)" }}>Client:</span>
              <span style={{ color: "var(--text-primary)" }}>{po.clientName}</span>

              <span style={{ color: "var(--text-secondary)" }}>Total Owed:</span>
              <strong style={{ color: "var(--text-primary)" }}>
                ${totalOwed.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </strong>

              <span style={{ color: "var(--text-secondary)" }}>Paid to Date:</span>
              <span style={{ color: "var(--text-primary)" }}>
                ${paidAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>

              <span style={{ color: "var(--text-secondary)", fontWeight: 600 }}>Remaining Balance:</span>
              <strong style={{ color: "var(--primary)" }}>
                ${remainingBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </strong>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="paymentAmount">Payment Amount ($) *</label>
            <input
              id="paymentAmount"
              type="number"
              step="0.01"
              min="0.01"
              max={remainingBalance}
              className="form-input"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              placeholder="0.00"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="paymentNote">Payment Memo / Note *</label>
            <input
              id="paymentNote"
              type="text"
              className="form-input"
              value={paymentNote}
              onChange={(e) => setPaymentNote(e.target.value)}
              placeholder="e.g. Received Check #1042 or Wire Transfer reference"
              required
            />
          </div>

          <div className="modal-footer" style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "1.5rem" }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Record Payment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
